import { kebabCase } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import './RMDAmount.scss';

import RMDToolTip from './RMDToolTip';
import config from '../../../config/config';
import { RMD_DISCLOSURE_MODAL } from '../../../redux/modules/modals/modals.module';
import { getHasRecurringRedemptions } from '../../../redux/modules/automaticInvestments/automaticInvestments.selectors';
import {
  getFirstRMDDeadline,
  getLastBusinessDayOfYear,
  getTotalRMDAmount,
  getYearUserTurningRMDEligible,
} from '../../../redux/modules/RMD/RMDSetting.selectors';
import { getRMDRemainingBalanceAmount } from '../../../redux/modules/sellTransaction/sellTransaction.selectors';
import If from '../../Shared/If/If';
import TRP from '../../Shared/TRP/TRP';
import {
  formatUnixWithSlash,
  futureYear,
  getCurrentYear,
} from '../../../utils/dates/date-utility';
import { currency } from '../../../utils/utils';
import errorSVG from '../../../assets/images/error.svg';
import { YEAR_2023 } from '../../../settings/settings';
import { getRMDRemainingBalanceAmountQCD } from '../../../redux/modules/qcd/qcd.selectors';
import { isQCD } from '../../../utils/transactions-util';
import { useDispatch } from 'react-redux';
import { openModal } from 'src/redux/modules/modals/modals.module';
import { isSalesforceRep } from '../../../redux/modules/authentication/authentication.module.selectors';

const RMDAmount = ({
  fund,
  hasRecurringRedemptions,
  RMDRemainingBalanceAmount,
  RMDSetting: { isLoaded },
  totalRMDAmount,
  yearUserTurningRMDEligible,
  customerWebViewEnabled = false,
  firstRMDDeadline,
  lastBusinessDayOfYear,
  currIsSalesForceRep = false,
}) => {
  const dispatch = useDispatch();
  const { ticker } = fund;
  const currentYear = getCurrentYear('YYYY');
  const isPostLaunch = (dateAndTime) => {
    const currentDate = new Date();
    const launchDate = new Date(dateAndTime);
    return currentDate > launchDate;
  };
  return (
    <div
      className={`rmd-amount-container e2e-rmd-amount-${kebabCase(
        ticker
      )} rmd-amount-container`}
    >
      <div className={`title`}>
        <h3 className={`title-text`}>
          {getCurrentYear('YYYY')} Remaining RMD
          <RMDToolTip className="rmd-amount-tablet-only" />
        </h3>
        {isCustViewIsSalesRepIsQCD(
          customerWebViewEnabled,
          currIsSalesForceRep
        ) ? (
          <></>
        ) : (
          <a
            className={`rmd-amount-rmd-dashboard rmd-amount-tablet-only`}
            href={`${config.url}/usis/rmd-tool`}
            rel="noopener noreferrer"
            target="_blank"
          >
            Visit RMD Dashboard
          </a>
        )}
      </div>
      <If test={!isLoaded}>
        <div className="rmd-amount-loading">Calculating...</div>
      </If>
      <If test={isLoaded}>
        <div className={`rmd-amount-section rmd-amount-rmd-amount-section`}>
          <div>
            <span
              className={`e2e-rmd-balance-amount rmd-amount-emphasize-amount`}
            >
              {currency(RMDRemainingBalanceAmount)}
            </span>
            <span className="rmd-amount-italics">
              {' '}
              of {currency(totalRMDAmount)}
            </span>
            <RMDToolTip />
          </div>
          {RMDRemainingBalanceAmount === 0 ? (
            <div className={`e2e-rmd-closeout rmd-amount-rmd-close-fund`}>
              <span>You have or will fulfill</span> your{' '}
              {getCurrentYear('YYYY')} RMD obligations at <TRP />
            </div>
          ) : (
            <div>
              {/* Before Toggle 1/1/2024 */}
              {!isPostLaunch('2024-01-01T00:01:00-05:00') ? (
                <div className="rmd-amount-deadline">
                  {' '}
                  Deadline:{' '}
                  {`${
                    yearUserTurningRMDEligible === currentYear &&
                    currentYear !== YEAR_2023
                      ? `4/1/${futureYear(1, 'YY')}`
                      : `12/29/${getCurrentYear('YY')}`
                  } `}
                </div>
              ) : (
                <div className="rmd-amount-deadline">
                  {' '}
                  Deadline:{' '}
                  {`${
                    yearUserTurningRMDEligible === currentYear
                      ? formatUnixWithSlash(firstRMDDeadline)
                      : formatUnixWithSlash(lastBusinessDayOfYear)
                  } `}
                </div>
              )}
              {hasRecurringRedemptions && (
                <div className="rmd-amount-note">
                  One-time transactions will not impact any scheduled or
                  recurring transactions.
                </div>
              )}
            </div>
          )}
          <div className={`rmd-amount-dashboard-link rmd-amount-desktop-only`}>
            {isCustViewIsSalesRepIsQCD(
              customerWebViewEnabled,
              currIsSalesForceRep
            ) ? (
              <></>
            ) : (
              <a
                className="rmd-amount-disclosure-link rmd-link-text"
                href={`${config.url}/usis/rmd-tool/`}
                rel="noopener noreferrer"
                target="_blank"
              >
                Visit RMD Dashboard
              </a>
            )}
          </div>
          <div className="rmd-amount-important-disclosure">
            {isCustViewIsSalesRepIsQCD(
              customerWebViewEnabled,
              currIsSalesForceRep
            ) ? (
              <></>
            ) : (
              <button
                className="rmd-amount-disclosure-link important-disclosure"
                onClick={() => dispatch(openModal(RMD_DISCLOSURE_MODAL))}
              >
                <img className="rmd-amount-error-image" alt="" src={errorSVG} />
                Important Disclosure
              </button>
            )}
          </div>
          <hr className="no-desktop-divider" />
        </div>
      </If>
    </div>
  );
};

function isCustViewIsSalesRepIsQCD(
  customerWebViewEnabled,
  currIsSalesForceRep
) {
  return !customerWebViewEnabled && currIsSalesForceRep && isQCD();
}

function mapStateToProps(state) {
  const { RMDSetting } = state;
  return {
    firstRMDDeadline: getFirstRMDDeadline(state),
    lastBusinessDayOfYear: getLastBusinessDayOfYear(state),
    hasRecurringRedemptions: getHasRecurringRedemptions(state),
    RMDRemainingBalanceAmount: isQCD()
      ? getRMDRemainingBalanceAmountQCD(state)
      : getRMDRemainingBalanceAmount(state),
    RMDSetting,
    totalRMDAmount: getTotalRMDAmount(state),
    yearUserTurningRMDEligible: getYearUserTurningRMDEligible(state),
    currIsSalesForceRep: isSalesforceRep(state),
  };
}

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);
export default compose(connect(mapStateToProps, mapDispatchToProps))(RMDAmount);
